'use client';

import { NextIntlClientProvider } from 'next-intl';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadMessages } from '../i18n/messages';
import {
  APP_LOCALE_CHANGED_EVENT,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  getStoredLocale,
  isAppLocale,
  saveStoredLocale,
  type AppLocale,
} from '../lib/locale';
import { subscribeAuthSession, getAuthSession } from '../lib/authSession';

type AppLocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
};

const AppLocaleContext = createContext<AppLocaleContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
});

export const useAppLocale = () => useContext(AppLocaleContext);

type AppLocaleProviderProps = {
  initialLocale: AppLocale;
  children: React.ReactNode;
};

export function AppLocaleProvider({ initialLocale, children }: AppLocaleProviderProps) {
  const [locale, setLocaleState] = useState<AppLocale>(initialLocale);

  // Hydrate from localStorage / session on mount and react to changes.
  useEffect(() => {
    const stored = getStoredLocale();
    if (stored !== locale) {
      setLocaleState(stored);
    }

    const handleAppLocaleChanged = () => {
      const next = getStoredLocale();
      setLocaleState((prev) => (prev === next ? prev : next));
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LOCALE_STORAGE_KEY) return;
      handleAppLocaleChanged();
    };

    window.addEventListener(APP_LOCALE_CHANGED_EVENT, handleAppLocaleChanged);
    window.addEventListener('storage', handleStorage);

    // Sync server-saved locale on session change (login/profile fetch).
    const unsubscribeSession = subscribeAuthSession((session) => {
      const memberLocale = session?.member.locale;
      if (!isAppLocale(memberLocale)) return;
      const current = getStoredLocale();
      if (memberLocale !== current) {
        saveStoredLocale(memberLocale);
      }
    });

    // Initial session check (in case session was restored before subscription).
    const initialSession = getAuthSession();
    const initialMemberLocale = initialSession?.member.locale;
    if (isAppLocale(initialMemberLocale) && initialMemberLocale !== getStoredLocale()) {
      saveStoredLocale(initialMemberLocale);
    }

    return () => {
      window.removeEventListener(APP_LOCALE_CHANGED_EVENT, handleAppLocaleChanged);
      window.removeEventListener('storage', handleStorage);
      unsubscribeSession();
    };
  }, [locale]);

  // Reflect current locale on the html element for accessibility / browser hints.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: AppLocale) => {
    saveStoredLocale(next);
    setLocaleState(next);
  }, []);

  const messages = useMemo(() => loadMessages(locale), [locale]);

  const contextValue = useMemo<AppLocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <AppLocaleContext.Provider value={contextValue}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone="Asia/Seoul">
        {children}
      </NextIntlClientProvider>
    </AppLocaleContext.Provider>
  );
}
